import { useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Global, css } from "@emotion/react";
import {
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListSubheader,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  MRT_Column,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_TableHeadCellFilterContainer,
  MRT_Updater,
  MRT_VisibilityState,
  useMaterialReactTable
} from "material-react-table";
import { differenceInHours, differenceInMinutes, format } from "date-fns";
import Iconify from "../../minimals/components/iconify";
import Label from "../../minimals/components/label";
import useKeyboardInset from "../../hooks/useKeyboardInset";
import { HeaderSlotContext } from "../components/layouts/dashboard/HeaderSlotContext";
import { FundingAuthorizationContext } from "./FundingAuthorizationContext";
import FedReferenceLine from "./FedReferenceLine";
import SmallControls from "./SmallControls";
import { PriorityLabels, PriorityStyles } from "./EditFunding/columns";
import { FundingPackage, FundingPackageLockMode, FundingPackageStatus, FundingPackageStatusConfig, isMadexOrder } from "./types";

const PAGE_SIZE = 30;

const StatusIcons: Record<FundingPackageStatus, string> = {
  [FundingPackageStatus.Draft]: "eva:edit-2-outline",
  [FundingPackageStatus.Requested]: "eva:clock-outline",
  [FundingPackageStatus.Deficient]: "eva:alert-triangle-fill",
  [FundingPackageStatus.Funded]: "eva:checkmark-circle-2-fill",
  [FundingPackageStatus.Cancelled]: "eva:close-circle-outline"
};

// These get their own menu buttons beside the search instead of a place in the filter sheet
const MENU_FILTERS: { id: string; icon: string }[] = [
  { id: "priority", icon: "eva:flag-outline" },
  { id: "status", icon: "eva:checkmark-circle-2-outline" }
];

const roundButtonSx = {
  width: 44,
  height: 44,
  color: "primary.dark",
  backgroundColor: "background.paper",
  border: "1px solid rgba(145, 158, 171, 0.48)",
  boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)",
  "&:hover": { backgroundColor: "background.paper" }
};

interface FilterMenuButtonProps {
  column: MRT_Column<FundingPackage>;
  icon: string;
}

// Reads options and single/multi choice from the desktop column, and writes the same filter value its header filter would
function FilterMenuButton({ column, icon }: FilterMenuButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const label = String(column.columnDef.header);
  const options = (column.columnDef.filterSelectOptions ?? []) as { label: string; value: unknown }[];
  const isMulti = column.columnDef.filterVariant === "multi-select";
  const filterValue = column.getFilterValue();
  const selected: unknown[] = Array.isArray(filterValue) ? filterValue : filterValue == null || filterValue === "" ? [] : [filterValue];

  const toggle = (value: unknown) => {
    if (isMulti) {
      const next = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];
      column.setFilterValue(next.length ? next : undefined);
    } else {
      column.setFilterValue(selected.includes(value) ? undefined : value);
      setAnchorEl(null);
    }
  };

  return (
    <>
      <Badge badgeContent={selected.length} color="primary" overlap="circular" sx={{ flexShrink: 0 }}>
        <IconButton
          aria-label={`${label}${selected.length ? ` (${selected.length} selected)` : ""}`}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={roundButtonSx}
        >
          <Iconify icon={icon} width={22} />
        </IconButton>
      </Badge>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 200, mt: -1 } } }}
      >
        <ListSubheader sx={{ lineHeight: "36px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {label}
          {selected.length > 0 && (
            <Button size="small" onClick={() => column.setFilterValue(undefined)}>
              Clear
            </Button>
          )}
        </ListSubheader>
        {options.map((option) => (
          <MenuItem key={String(option.value)} selected={selected.includes(option.value)} onClick={() => toggle(option.value)}>
            {isMulti && <Checkbox size="small" checked={selected.includes(option.value)} sx={{ p: 0, mr: 1.5 }} />}
            {option.label}
            {!isMulti && selected.includes(option.value) && (
              <Iconify icon="eva:checkmark-fill" width={18} sx={{ ml: "auto", flexShrink: 0, color: "primary.main" }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

const formatSubmitted = (value: Date | string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  const hours = differenceInHours(new Date(), date);
  const ago = hours === 0 ? `${differenceInMinutes(new Date(), date) % 60} min ago` : `${hours} hr${hours > 1 ? "s" : ""} ago`;
  return `${format(date, "MMM d, h:mm a")} · ${ago}`;
};

interface Props {
  title: string;
  loading: boolean;
  // Already narrowed by the page's default status window
  packages: FundingPackage[];
  // The desktop table's columns and filter state, so the filter sheet offers the same filters
  columns: MRT_ColumnDef<FundingPackage>[];
  columnFilters: MRT_ColumnFiltersState;
  onColumnFiltersChange: (updater: MRT_Updater<MRT_ColumnFiltersState>) => void;
  columnVisibility: MRT_VisibilityState;
  onNewPackage?: () => void;
}

// Phone layout for /funding-packages: newest first, 1031 (Madex) only by default, one card per package,
// with the search pill and filter sheet used on the other mobile pages
export default function MobileFundingPackages({
  title,
  loading,
  packages,
  columns,
  columnFilters,
  onColumnFiltersChange,
  columnVisibility,
  onNewPackage
}: Props) {
  const { slotEl } = useContext(HeaderSlotContext);
  const { fedReferences, madexOnly, setMadexOnly } = useContext(FundingAuthorizationContext);
  const keyboardInset = useKeyboardInset(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filterResetKey, setFilterResetKey] = useState(0);

  const data = useMemo(() => (madexOnly ? packages.filter((p) => isMadexOrder(p.orderNumber)) : packages), [packages, madexOnly]);

  // Never rendered as a table; the cards read its filtered, sorted rows
  const table = useMaterialReactTable({
    columns,
    data,
    enableGlobalFilter: true,
    // Plain substring search; MRT's default fuzzy match returns loose hits
    globalFilterFn: "contains",
    enableFacetedValues: true,
    enableSortingRemoval: false,
    onColumnFiltersChange,
    initialState: {
      sorting: [{ id: "submitted", desc: true }],
      columnVisibility
    },
    // showColumnFilters un-collapses the header filters the sheet renders
    state: { columnFilters, showColumnFilters: true }
  });

  const { globalFilter } = table.getState();
  const rows = table.getPrePaginationRowModel().rows;
  const isMenuFilter = (id: string) => MENU_FILTERS.some((f) => f.id === id);
  const filterableHeaders = table.getLeafHeaders().filter((header) => header.column.getCanFilter() && !isMenuFilter(header.id));
  const menuColumns = MENU_FILTERS.map((f) => ({ ...f, column: table.getColumn(f.id) })).filter((f) => f.column?.getCanFilter());
  // The 1031 switch and the menu buttons show their own state, so only the sheet's filters count here
  const activeFilterCount = columnFilters.filter((f) => !isMenuFilter(f.id)).length;

  return (
    <SmallControls>
      <>
        <Global
          styles={css`
            main {
              /* The page heading moves into the fixed 64px header */
              padding-top: 64px !important;
            }
          `}
        />

        {slotEl &&
          createPortal(
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: 1, minWidth: 0, gap: 1 }}>
              <Typography noWrap sx={{ color: "primary.dark", fontSize: 17, fontWeight: 700, lineHeight: 1.2, minWidth: 0 }}>
                {title}
              </Typography>
              {onNewPackage && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Iconify icon="eva:plus-fill" width={16} />}
                  onClick={onNewPackage}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  New
                </Button>
              )}
            </Stack>,
            slotEl
          )}

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1.5 }}>
          <FormControlLabel
            control={<Switch size="small" checked={madexOnly} onChange={(_, value) => setMadexOnly(value)} />}
            label="1031 only"
            sx={{ ml: 0 }}
          />
          <Typography variant="body2" color="text.secondary">
            {loading ? "" : `${rows.length} package${rows.length === 1 ? "" : "s"}`}
          </Typography>
        </Stack>

        {/* Clearance for the search pill (44 tall + 12 offset + breathing room) */}
        <Stack spacing={1.5} sx={{ pb: "calc(72px + env(safe-area-inset-bottom))" }}>
          {loading && [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={112} />)}

          {!loading && rows.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 6 }}>
              {madexOnly ? "No 1031 packages to show" : "No packages to show"}
            </Typography>
          )}

          {!loading &&
            rows.slice(0, visibleCount).map(({ original: pkg }) => {
              const status = FundingPackageStatusConfig[pkg.status];
              const priority = PriorityStyles[pkg.priority];
              const lock = pkg.activeLock;
              return (
                <Card key={pkg.id} variant="outlined" sx={{ borderColor: "grey.400" }}>
                  <CardActionArea component={Link} to={`/edit-funding-package/${pkg.orderNumber}-pkg${pkg.packageNumber}`} sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      {/* On narrow phones the status wraps under the order number rather than truncating it */}
                      <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ minWidth: 0, columnGap: 1, rowGap: 0.5 }}>
                        <Typography variant="subtitle1" noWrap sx={{ minWidth: 0 }}>
                          {pkg.orderNumber}
                          {pkg.packageNumber > 1 && (
                            <Typography component="span" variant="body2" color="text.secondary">
                              {" "}
                              · Pkg {pkg.packageNumber}
                            </Typography>
                          )}
                        </Typography>
                        <Label
                          variant="soft"
                          color={status.color as any}
                          startIcon={<Iconify icon={StatusIcons[pkg.status]} />}
                          sx={{ flexShrink: 0 }}
                        >
                          {status.label}
                        </Label>
                      </Stack>
                      {priority && (
                        <Chip
                          size="small"
                          label={PriorityLabels[pkg.priority]}
                          variant="outlined"
                          sx={{ color: priority.color, borderColor: priority.border, bgcolor: priority.bg, fontWeight: 600, flexShrink: 0 }}
                        />
                      )}
                    </Stack>

                    {lock && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={lock.mode === FundingPackageLockMode.Review ? "info" : "warning"}
                        label={`${lock.lockedByName || lock.lockedByEmail} ${lock.mode === FundingPackageLockMode.Review ? "reviewing" : "editing"}`}
                        sx={{ mt: 1 }}
                      />
                    )}

                    {pkg.status === FundingPackageStatus.Funded && (
                      <Box sx={{ mt: 0.75 }}>
                        <FedReferenceLine fedReferences={fedReferences[pkg.id]} />
                      </Box>
                    )}

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Submitted {formatSubmitted(pkg.requestedDate)}
                    </Typography>
                  </CardActionArea>
                </Card>
              );
            })}

          {!loading && rows.length > visibleCount && (
            <Button variant="outlined" onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}>
              Show more ({rows.length - visibleCount})
            </Button>
          )}
        </Stack>

        {/* Same floating search bar as /documents, the phonebook and Orders */}
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (theme) => theme.zIndex.drawer - 1,
            px: 1.5,
            pt: keyboardInset > 0 ? 1.5 : 4,
            pb: keyboardInset > 0 ? 1 : "calc(12px + env(safe-area-inset-bottom))",
            transform: `translateY(-${keyboardInset}px)`,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
            backgroundColor: keyboardInset > 0 ? "background.paper" : undefined,
            boxShadow: keyboardInset > 0 ? "0 -6px 18px rgba(22, 28, 36, 0.14)" : undefined,
            background:
              keyboardInset > 0
                ? undefined
                : (theme) =>
                    `linear-gradient(to top, ${theme.palette.background.default} 55%, ${alpha(theme.palette.background.default, 0)})`
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pointerEvents: "auto", width: 1, maxWidth: 560 }}>
            <Badge badgeContent={activeFilterCount} color="primary" overlap="circular" sx={{ flexShrink: 0 }}>
              <IconButton
                aria-label={`Filters${activeFilterCount ? ` (${activeFilterCount} active)` : ""}`}
                onClick={() => setIsFilterDrawerOpen(true)}
                sx={roundButtonSx}
              >
                <Iconify icon="ic:round-filter-list" width={22} />
              </IconButton>
            </Badge>
            <TextField
              fullWidth
              placeholder="Search"
              value={globalFilter ?? ""}
              onChange={(event) => {
                table.setGlobalFilter(event.target.value || undefined);
                setVisibleCount(PAGE_SIZE);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" width={20} />
                  </InputAdornment>
                ),
                endAdornment: globalFilter ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label="Clear search"
                      onClick={() => table.setGlobalFilter(undefined)}
                      sx={{ color: "text.disabled" }}
                    >
                      <Iconify icon="eva:close-circle-fill" width={18} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 44,
                  borderRadius: 999,
                  backgroundColor: "background.paper",
                  boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)"
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(145, 158, 171, 0.48)"
                },
                // 16px floor: below that iOS zooms the page on focus and never zooms back.
                "& .MuiInputBase-input": { fontSize: 16 }
              }}
            />
            {menuColumns.map(({ id, icon, column }) => (
              <FilterMenuButton key={id} column={column} icon={icon} />
            ))}
          </Stack>
        </Box>

        {isFilterDrawerOpen && (
          <Global
            styles={css`
              /* Short option lists; tighter rows than MUI's 48px touch default show them all at once */
              .MuiAutocomplete-popper .MuiAutocomplete-option,
              .MuiMenu-list .MuiMenuItem-root {
                min-height: 40px;
              }
            `}
          />
        )}

        <Drawer
          anchor="bottom"
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          PaperProps={{
            sx: { maxHeight: "85vh", borderRadius: "20px 20px 0 0", pb: "env(safe-area-inset-bottom)" }
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>Filters</Typography>
              {/* Updates live as filters change, so the effect is visible without closing */}
              <Typography variant="caption" color="text.secondary" noWrap>
                {rows.length} of {packages.length} packages
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Button
                size="small"
                onClick={() => {
                  onColumnFiltersChange((prev) => prev.filter((f) => isMenuFilter(f.id)));
                  // Remount the filter fields so they show the cleared values
                  setFilterResetKey((key) => key + 1);
                }}
              >
                Clear all
              </Button>
              <IconButton size="small" aria-label="Close filters" onClick={() => setIsFilterDrawerOpen(false)}>
                <Iconify icon="eva:close-fill" />
              </IconButton>
            </Stack>
          </Stack>
          <Stack
            spacing={2}
            sx={{
              p: 2,
              overflowY: "auto",
              overscrollBehavior: "contain",
              // Header filters are sized to their column; here they take the full width.
              "& .MuiFormControl-root, & .MuiTextField-root, & .MuiAutocomplete-root": {
                width: "100% !important",
                minWidth: "0 !important"
              },
              // 16px floor so iOS doesn't zoom on focus.
              "& .MuiInputBase-input": { fontSize: 16 },
              "& .MuiOutlinedInput-root:not(.Mui-focused) .MuiOutlinedInput-notchedOutline": {
                borderColor: "grey.500"
              }
            }}
          >
            {filterableHeaders.map((header) => (
              <Box key={`${header.id}-${filterResetKey}`}>
                <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: 700, color: "text.secondary" }}>
                  {header.column.columnDef.header}
                </Typography>
                <MRT_TableHeadCellFilterContainer header={header} table={table} />
              </Box>
            ))}
          </Stack>
        </Drawer>
      </>
    </SmallControls>
  );
}
